import { IFilterParams, IDoesFilterPassParams } from 'ag-grid-community';
import { BehaviorSubject, Observable } from 'rxjs';
import { map } from 'rxjs/operators';
import { AbstractFilterComponent } from './ag-filter';
import { ExceptionMessage } from '@app/constants/exceptions.constants';
import { changeFilterCallback } from '../functions';
import { IColumnCheckboxFilterModel } from '../models/ag-filter';
import { MonthlyField } from '@app/monthly-report/monthly-report/constants';

interface CheckboxIFilterParams extends IFilterParams {
  sortComparator: () => number;
}

const NULL_CHECKBOX_OPTION_NAME = 'None';

export class AbstractCheckboxFilterComponent extends AbstractFilterComponent {
  protected _checkedValues: string[];
  protected _sortComparator: () => number;

  protected _isAllCheckedSubject = new BehaviorSubject<boolean>(true);
  readonly isAllChecked$ = this._isAllCheckedSubject.asObservable();

  protected _valuesSubject = new BehaviorSubject<string[]>([]);
  readonly values$ = this._valuesSubject.asObservable();

  agInit(params: CheckboxIFilterParams): void {
    super.agInit(params);
    this._valuesSubject.next(this._getUniqueValuesForColumn(params.column.getColId()));
    this._checkedValues = [...this._valuesSubject.value];

    if (!params.sortComparator) {
      throw new ReferenceError(ExceptionMessage.SortComparatorUndefined);
    } else {
      this._sortComparator = params.sortComparator;
    }
  }

  isFilterActive(): boolean {
    return !this._isAllCheckedSubject.value;
  }

  doesFilterPass(params: IDoesFilterPassParams): boolean {
    return this._doesCheckboxFilterPass(params);
  }

  getModel(): IColumnCheckboxFilterModel {
    return { checkboxValue: this._checkedValues };
  }

  setModel(model: IColumnCheckboxFilterModel): void {
    this._checkedValues = model?.checkboxValue ? model.checkboxValue : [...this._valuesSubject.value];
    this._updateAllCheckedOnCheckboxChange();
  }

  onNewRowsLoaded(): void {
    this._valuesSubject.next(this._getUniqueValuesForColumn(this._params.column.getColId()));
    this._checkedValues = this._checkedValues.filter((checkedValue) =>
      this._valuesSubject.value.includes(checkedValue)
    );
    if (!Object.keys(this._gridApi.getFilterModel()).length) {
      this._checkedValues = [...this._valuesSubject.value];
      this._isAllCheckedSubject.next(true);
    }
  }

  onAllCheckboxChange(checked: boolean): void {
    this._checkedValues = checked ? [...this._valuesSubject.value] : [];
    this._isAllCheckedSubject.next(checked);
    changeFilterCallback(this._params);
  }

  onCheckboxChange(value: string, checked: boolean): void {
    const values = this._valuesSubject.value;

    if (checked) {
      if (!!this._isAllCheckedSubject.value) {
        this._checkedValues = [value];
      } else {
        this._checkedValues.push(value);
      }
      if (values.length === 1) {
        this._isAllCheckedSubject.next(true);
      }
    } else {
      this._checkedValues = [...this._checkedValues.filter((v) => v !== value)];
    }

    this._updateAllCheckedOnCheckboxChange();
    changeFilterCallback(this._params);
  }

  isCheckboxChecked(value: string | number): Observable<boolean> {
    const stringValue = `${value}`;

    return this.isAllChecked$.pipe(
      map((allChecked) => {
        return this._checkedValues.includes(stringValue) ? true : false;
      })
    );
  }

  protected _updateAllCheckedOnCheckboxChange(): void {
    const values = this._valuesSubject.value;

    if (this._isAllValuesChecked(values) || !Object.keys(this._params.api.getFilterModel()).length) {
      this._isAllCheckedSubject.next(true);
    } else {
      this._isAllCheckedSubject.next(false);
    }
  }

  protected _getUniqueValuesForColumn(colId: string): string[] {
    const valuesForColumn: string[] = [];

    this._gridApi.forEachLeafNode((node) => {
      const value =
        this._gridApi.getValue(colId, node) !== null ? this._gridApi.getValue(colId, node) : NULL_CHECKBOX_OPTION_NAME;
      const hourColumns: string[] = [MonthlyField.CreativeHours, MonthlyField.TotalHours];
      const formattedValue = hourColumns.includes(colId) ? value.toFixed(2) : value;

      valuesForColumn.push(`${formattedValue}`);
    });

    return this._sortUniqueValues([...new Set(valuesForColumn)]);
  }

  protected _getUniqueValuesForNoLeafColumn(colId: string): string[] {
    const valuesForColumn: string[] = [];

    this._gridApi.forEachNode((node) => {
      valuesForColumn.push(`${this._gridApi.getValue(colId, node)}`);
    });

    return this._sortUniqueValues([...new Set(valuesForColumn)]);
  }

  protected _sortUniqueValues(values: string[]): string[] {
    return values.sort(this._sortComparator);
  }

  protected _doesCheckboxFilterPass(params: IDoesFilterPassParams): boolean {
    const value = this._valueGetter(params.node) !== null ? this._valueGetter(params.node) : NULL_CHECKBOX_OPTION_NAME;
    const field = this._params.column.getColId();
    const isHourColumn = field === MonthlyField.CreativeHours || field === MonthlyField.TotalHours;
    const formattedValue = isHourColumn ? value?.toFixed(2).toString() : value?.toString();

    return this._checkedValues.includes(formattedValue);
  }

  private _isAllValuesChecked(values: string[]): boolean {
    return this._checkedValues.length === values.length;
  }
}
